import { faCopy, faLink, faMoneyBillTransfer, faUsers, faWallet } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Page } from '~/components/Page'
import {
    Chip,
    EmptyBlock,
    ErrorBlock,
    Panel,
    PRIZE_GREEN,
    SectionHeading,
    Skeleton,
    Stat,
} from '~/components/ui'
import { AffiliateDashboard, Statement, useAffiliateDashboard } from '../api/useAffiliateDashboard'

/**
 * Money for display.
 *
 * The API sends Decimal as a string on purpose. This parses once, at the edge, to
 * put a currency in front of it — the value is never fed back to the server, so
 * the float cannot travel anywhere it would matter.
 */
const usd = (value: string | undefined) => {
    // `$${n}` puts the sign inside the currency for a loss -- "$-28.00". Formatting
    // the magnitude and placing the sign outside gives the "-$28.00" a reader expects.
    const amount = Number(value ?? 0) || 0
    const formatted = Math.abs(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })
    return `${amount < 0 ? '-' : ''}$${formatted}`
}

const percent = (rate: string | undefined) => `${Math.round((Number(rate ?? 0) || 0) * 100)}%`

const day = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })

/** The referral link, and the one thing you do with it. */
const LinkPanel = ({ url }: { url: string }) => {
    const [copied, setCopied] = useState(false)

    return (
        <Panel>
            <div className='flex flex-col gap-3 p-4 md:p-5'>
                <span className='text-2xs font-bold uppercase tracking-[0.2em] text-white/40'>Your link</span>
                <button
                    onClick={() => {
                        navigator.clipboard.writeText(url)
                        setCopied(true)
                        setTimeout(() => setCopied(false), 800)
                    }}
                    title='Copy referral link'
                    className={twMerge(
                        'group flex min-h-[2.75rem] w-full flex-row items-center justify-between gap-3 px-3 py-2',
                        'border border-white/15 bg-black/40 transition-colors hover:border-white/40'
                    )}
                >
                    {/* Scrolls rather than wraps — a wrapped URL changes the row height on a phone
                        and shoves everything below it around. Same treatment as `Referral.tsx`. */}
                    <span className='min-w-0 flex-1 overflow-x-auto no-scrollbar whitespace-nowrap text-left text-xs md:text-sm'>
                        {url}
                    </span>
                    <span
                        className={twMerge(
                            'flex shrink-0 flex-row items-center gap-2 text-2xs font-black uppercase tracking-[0.2em]',
                            copied ? 'text-[#00FF90]' : 'text-white/45 group-hover:text-white'
                        )}
                    >
                        <FontAwesomeIcon icon={faCopy} />
                        {copied ? 'Copied' : 'Copy'}
                    </span>
                </button>
            </div>
        </Panel>
    )
}

/**
 * One period's money.
 *
 * Both halves of the arithmetic are shown, not just the total. An affiliate who
 * can see fees collected and winnings paid can check the profit figure themselves,
 * which is the difference between a dashboard and a number we ask them to believe.
 */
const StatementPanel = ({ title, hint, statement }: { title: string; hint?: string; statement: Statement }) => (
    <section className='flex flex-col gap-3 md:gap-4'>
        <SectionHeading icon={faWallet} title={title} hint={hint} />
        <Panel>
            <div className='flex flex-col gap-5 p-4 md:p-6'>
                <div className='flex flex-col gap-1'>
                    <span className='text-2xs uppercase tracking-[0.2em] text-white/40'>
                        Your share ({percent(statement.rate)})
                    </span>
                    <span
                        className='font-monument text-3xl font-bold leading-none tabular-nums md:text-4xl'
                        style={{ color: PRIZE_GREEN }}
                    >
                        {usd(statement.share)}
                    </span>
                </div>

                <div className='grid grid-cols-2 gap-4 border-t border-white/10 pt-4 sm:grid-cols-3'>
                    <Stat label='Spawn fees' value={usd(statement.fees_collected)} valueClassName='text-lg' />
                    <Stat label='Winnings paid out' value={usd(statement.paid_out)} valueClassName='text-lg' />
                    <Stat
                        label='Profit'
                        value={usd(statement.profit)}
                        valueClassName={twMerge('text-lg', statement.is_negative_period && 'text-white/50')}
                    />
                </div>

                {statement.is_negative_period && (
                    <p className='text-xs font-medium leading-relaxed text-white/45'>
                        Your players won more than they spent this period, so there is nothing to pay out yet.
                        Nothing is owed by you — it simply nets against your next period.
                    </p>
                )}

                {statement.capped && (
                    <p className='text-xs font-medium leading-relaxed text-white/45'>
                        Capped at BR1 take. Your share is never more than BR1 actually kept from your players,
                        after the winnings it paid to them and to the players who beat them.
                    </p>
                )}
            </div>
        </Panel>
    </section>
)

const Funnel = ({ funnel }: { funnel: AffiliateDashboard['funnel'] }) => (
    <div className='grid grid-cols-3 gap-3 md:gap-4'>
        {(
            [
                // "Link visits" and not "visitors": the counter behind this counts
                // sessions and is not deduplicated, so calling it people would be a lie.
                ['Link visits', funnel.visits],
                ['Signups', funnel.signups],
                ['Still playing', funnel.active_players],
            ] as Array<[string, number]>
        ).map(([label, value]) => (
            <Panel key={label} size={12}>
                <div className='flex flex-col gap-1.5 p-4 text-center'>
                    <span className='text-2xs font-bold uppercase tracking-[0.2em] text-white/40'>{label}</span>
                    <span className='text-xl font-black tabular-nums md:text-2xl'>{value.toLocaleString()}</span>
                </div>
            </Panel>
        ))}
    </div>
)

const Referrals = ({ rows }: { rows: AffiliateDashboard['referrals'] }) => (
    <section className='flex flex-col gap-3 md:gap-4'>
        <SectionHeading icon={faUsers} title='Your players' hint={`${rows.length} total`} />
        {rows.length === 0 ? (
            <EmptyBlock title='Nobody yet'>
                Share your link. Anyone who signs up through it shows up here, permanently.
            </EmptyBlock>
        ) : (
            <Panel>
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead>
                            <tr className='border-b border-white/10 text-2xs uppercase tracking-[0.15em] text-white/40'>
                                <th className='p-3 text-left font-bold md:p-4'>Player</th>
                                <th className='p-3 text-left font-bold md:p-4'>Joined</th>
                                <th className='p-3 text-right font-bold md:p-4'>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.referred_user__username} className='border-b border-white/5 last:border-0'>
                                    {/* Stored case. Uppercasing a username is wrong. */}
                                    <td className='p-3 font-medium md:p-4'>{row.referred_user__username}</td>
                                    <td className='p-3 text-white/50 md:p-4'>{day(row.created_at)}</td>
                                    <td className='p-3 text-right md:p-4'>
                                        {row.is_active_player > 0 ? (
                                            <Chip className='border-[#00FF90]/30 text-[#00FF90]'>Playing</Chip>
                                        ) : (
                                            <Chip className='text-white/40'>Inactive</Chip>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>
        )}
    </section>
)

const Payouts = ({ rows }: { rows: AffiliateDashboard['payouts'] }) => (
    <section className='flex flex-col gap-3 md:gap-4'>
        <SectionHeading icon={faMoneyBillTransfer} title='Payouts' />
        {rows.length === 0 ? (
            <EmptyBlock title='No payments yet'>
                Statements close monthly and are reviewed by a person before payment. Every payment we make will
                be listed here.
            </EmptyBlock>
        ) : (
            <Panel>
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead>
                            <tr className='border-b border-white/10 text-2xs uppercase tracking-[0.15em] text-white/40'>
                                <th className='p-3 text-left font-bold md:p-4'>Period</th>
                                <th className='p-3 text-left font-bold md:p-4'>Paid</th>
                                <th className='p-3 text-right font-bold md:p-4'>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={`${row.period_start}-${row.paid_at}`} className='border-b border-white/5 last:border-0'>
                                    <td className='p-3 md:p-4'>
                                        {day(row.period_start)} – {day(row.period_end)}
                                    </td>
                                    <td className='p-3 text-white/50 md:p-4'>{day(row.paid_at)}</td>
                                    <td
                                        className='p-3 text-right font-black tabular-nums md:p-4'
                                        style={{ color: PRIZE_GREEN }}
                                    >
                                        {usd(row.amount)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>
        )}
    </section>
)

export const AffiliateToolsPage = () => {
    const { data, isLoading, isError, refetch } = useAffiliateDashboard()

    return (
        <Page title='Affiliate Tools' header='Affiliate Tools'>
            <div className='mx-auto flex w-full max-w-[1100px] flex-col gap-10 px-4 py-6 md:gap-14 md:px-8 md:py-10'>
                <h1 className='sr-only'>Affiliate Tools</h1>

                {isError ? (
                    <ErrorBlock onRetry={() => refetch()}>Your affiliate dashboard could not be loaded.</ErrorBlock>
                ) : isLoading || !data ? (
                    <div className='flex flex-col gap-4'>
                        <Skeleton className='h-28 w-full' />
                        <Skeleton className='h-24 w-full' />
                        <Skeleton className='h-64 w-full' />
                    </div>
                ) : (
                    <>
                        <section className='flex flex-col gap-3 md:gap-4'>
                            <SectionHeading icon={faLink} title='Referral link' />
                            <LinkPanel url={data.url} />
                            <Funnel funnel={data.funnel} />
                        </section>

                        <StatementPanel title='This month' hint='So far' statement={data.current_period} />
                        <StatementPanel title='Lifetime' statement={data.lifetime} />
                        <Referrals rows={data.referrals} />
                        <Payouts rows={data.payouts} />
                    </>
                )}
            </div>
        </Page>
    )
}
